import type { ValidationResult } from "@react-types/shared";
import { clsx } from "clsx";
import type { FC, PropsWithChildren } from "react";
import { type AriaCheckboxGroupProps, useCheckboxGroup } from "react-aria";
import { useCheckboxGroupState } from "react-stately";

import formStyle from "../../styles/form.module.css";
import { Badge } from "../Badge/Badge.tsx";
import { Description } from "../Description/Description.tsx";
import { Label } from "../Label/Label.tsx";
import { HStack } from "../Stack/HStack.tsx";
import { Spacer } from "../Stack/Spacer.tsx";
import { VStack } from "../Stack/VStack.tsx";
import { CheckboxGroupContext } from "./settings.ts";

export const CheckboxGroup: FC<PropsWithChildren<AriaCheckboxGroupProps>> = (props) => {
  const state = useCheckboxGroupState(props);
  const { groupProps, labelProps, descriptionProps, errorMessageProps } = useCheckboxGroup(props, state);

  return (
    <VStack spacing={2} {...groupProps}>
      {props.label != undefined && (
        <HStack style={{ alignItems: "baseline" }}>
          <Label {...labelProps}>{props.label}</Label>
          <Spacer />
          {!!props.isReadOnly && <Badge intent="light" large inline label="read only" style={{ flex: "0 0 auto" }} />}
          {!props.isRequired && !props.isReadOnly && (
            <Badge intent="light" large inline label="optional" style={{ flex: "0 0 auto" }} />
          )}
        </HStack>
      )}
      <Description
        {...descriptionProps}
        data-testid="checkbox-field-description"
        className={clsx(formStyle.input_layout_column1, {
          [formStyle.description_with_tag]: props.label != undefined && (props.isReadOnly || !props.isRequired),
        })}
      >
        {props.description}
      </Description>
      <VStack spacing={3} style={{ marginTop: 2, flex: 0, minWidth: 17 }}>
        <CheckboxGroupContext.Provider value={state}>{props.children}</CheckboxGroupContext.Provider>
      </VStack>
      {props.errorMessage != undefined && !!props.isInvalid && !props.isDisabled && (
        <div role="alert" {...errorMessageProps} className={formStyle.error_message} data-testid="number-field-error">
          {typeof props.errorMessage === "function" ? props.errorMessage({} as ValidationResult) : props.errorMessage}
        </div>
      )}
    </VStack>
  );
};
